import React, { useMemo } from 'react';
import cn from 'classnames';
import { useLocation } from '@reach/router';
import Link from 'components/UI/Link';
import Title from 'components/UI/Title';
import LazyImage from 'components/UI/LazyImage';
import Tag from 'components/UI/Tag';
import Image from 'components/UI/Image';
import { useTrans } from 'utils/localisation';
import ReviewCard from './ReviewCard';
import DefaultReviewCard from './DefaultReviewCard';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './Intro.module.scss';

function Intro({
  title,
  showButton = true,
  logo,
  tags,
  image,
  review,
  displayReview = true,
  firstTabLabel,
  setActive,
  isNda,
}) {
  const t = useTrans(['common', 'reviewShield', 'nda']);
  const { state } = useLocation();

  const link = useMemo(() => {
    if (state?.prevPath) {
      return {
        name: t('backToPreviousPage'),
        url: `${state?.prevPath}#concepts-list`,
      };
    }
    return { name: t('seeMoreProjects'), url: '/case-studies' };
  }, [state]);

  const caseLogo = logo?.color ?? logo?.white;

  return (
    <section className={styles.container}>
      <div className={styles.layout}>
        {link && showButton && (
          <Link href={link.url} className={styles.link} onClick={link.handleClick}>
            {link.name}
          </Link>
        )}
        {isNda ? (
          <div className={styles.ndaWrapper}>
            <div className={styles.nda}>
              <div className={styles.ndaLogoText}>nda</div>
            </div>
            <p className={styles.ndaText}>{t('ndaCustomerText')}</p>
          </div>
        ) : (
          caseLogo && (
            <LazyImage
              className={cn(styles.logo, !logo.color && styles.grayFilter)}
              src={caseLogo}
              alt={title}
            />
          )
        )}
        <div className={cn(styles.titleRow, !displayReview && styles.withoutReview)}>
          <div className={styles.titleLeft}>
            <Title tag="h1" size="h1Cases" className={styles.title}>
              {title}
            </Title>
            {tags && (
              <div className={styles.tagsRow}>
                {tags?.map(industry => (
                  <Tag key={industry} theme="oval">
                    {industry}
                  </Tag>
                ))}
              </div>
            )}
          </div>
          {displayReview &&
            (review ? (
              <ReviewCard
                className={styles.reviewCard}
                firstTabLabel={firstTabLabel}
                setActive={setActive}
                {...review}
              />
            ) : (
              <DefaultReviewCard className={styles.reviewCard} />
            ))}
        </div>
        {image && (
          <Image
            alt={title}
            src={image}
            className={styles.picture}
            classImage={styles.image}
            lazy={false}
          />
        )}
      </div>
    </section>
  );
}

Intro.displayName = 'Intro';

export default withErrorBoundary(Intro);
